import Button from "@shared/components/ui/Button";
import { TestProgressStatus } from "@shared/enums/test.enum";
import theme from "@shared/utils/themeColors";
import UserImage from "../../../images/user-128-01.jpg";
import {
  getStatusBadgeClass,
  getStatusText,
} from "../../../shared/utils/test.utils";
import { Test } from "../types/test.types";

interface DiagnosticTestCardProps {
  test: Test;
  onStart: (testId: string, resume?: boolean) => void;
}

const DiagnosticTestCard = ({ test, onStart }: DiagnosticTestCardProps) => {
  const hasProgress =
    test.currentProgress &&
    test.currentProgress.questionsAnswered &&
    test.currentProgress.questionsAnswered > 0;
  const progressPercentage = hasProgress
    ? Math.round(
        (test.currentProgress!.questionsAnswered! / test.totalQuestions!) * 100
      )
    : 0;

  return (
    <div
      className={`col-span-full ${theme.bg.primary} shadow-lg rounded-xl overflow-hidden`}
    >
      <div className="flex flex-col md:flex-row h-full">
        <div className="md:w-2/5 relative shadow-xl transition">
          <img
            src={test.coverImage || UserImage}
            alt="Diagnostic Test"
            className="w-full h-full object-cover"
          />
          <div className="absolute top-4 left-4">
            <div
              className={`text-sm inline-flex font-medium rounded-full text-center px-3 py-1.5 ${getStatusBadgeClass(
                test.status
              )}`}
            >
              {getStatusText(test.status)}
            </div>
          </div>
        </div>
        <div className="p-8 md:w-3/5 flex flex-col transition">
          <div className="grow">
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
              {test.name}
            </h2>
            <p className="text-slate-600 dark:text-slate-300 mb-4">
              This diagnostic test will help us understand your current skill
              level and provide personalized recommendations for your study
              plan.
            </p>
            <div className="text-sm mb-1">
              Total Questions: {test.totalQuestions}
            </div>
            <div className="text-sm mb-4">
              Estimated Time: {Math.round(test.totalQuestions * 1.5)} minutes
            </div>

            {test.status === TestProgressStatus.COMPLETED && (
              <div className="text-lg text-emerald-600 font-semibold mb-2">
                Score: {test.score}%
              </div>
            )}

            {hasProgress && test.status !== TestProgressStatus.COMPLETED ? (
              <div className="mb-4">
                <div className="w-full bg-gray-200 rounded-full mt-1 overflow-hidden">
                  <div
                    className={`${theme.bg.brand} h-3 rounded-full`}
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>

                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300 mt-1">
                  <span>
                    Answered: {test.currentProgress!.questionsAnswered}
                  </span>
                  <span>{progressPercentage}%</span>
                </div>
              </div>
            ) : null}
          </div>

          <div className="mt-4">
            {test.status === TestProgressStatus.COMPLETED ? (
              <Button onClick={() => onStart(test.id)}>View Results</Button>
            ) : hasProgress ? (
              <Button onClick={() => onStart(test.id, true)}>
                Continue Test
              </Button>
            ) : (
              <Button onClick={() => onStart(test.id)}>
                Start Diagnostic Test
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiagnosticTestCard;
